import React, { memo, useMemo, useEffect, useState } from "react";
import "./FormattedRes.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw"; // Allow raw HTML
import supersub from "remark-supersub";
import remarkDeflist from "remark-deflist";
import "highlight.js/styles/default.css";
import remarkParse from "remark-parse";
import CustomLink from "./CustomLink";
import {
  actionQuestionPlugin,
  componentsContainer,
  replaceUnicodeSymbols,
} from "./plugin";
import {logDebug, logTrace, logCritical, logWarning} from "../../../../utils/logger";
import { v4 } from "uuid";
import { useAppContext } from "../../../../AppDataContext";

interface FormattedResProps {
  children: string;
  questionHandler?: (questionText: string) => void;
  load: boolean;
  wrappedParams: any;
  stopStreaming?: boolean;
}

export const FormattedRes: React.FC<FormattedResProps> = memo(
  ({ children, questionHandler, load, wrappedParams, stopStreaming }) => {
    const appData = useAppContext();

    // finalContent holds the full text to be rendered via ReactMarkdown.
    const [finalContent, setFinalContent] = useState<string>(children);
    // flashChunk holds the new text chunk that should be animated.
    const [flashChunk, setFlashChunk] = useState<string>(children);

    // For debugging: log whenever the flash chunk changes.
    useEffect(() => {
      if (flashChunk) {
        logTrace("New flash chunk:", flashChunk);
      }
    }, [flashChunk]);

    // When children changes, update finalContent.
    useEffect(() => {
      setFinalContent(children);
      setFlashChunk("");
    }, [children]);

    // Callback that should be called by your streaming logic with each new chunk.
    // For now, you can simulate an update by manually calling this callback.
    const updateFlashChunk = (chunk: string) => {
      setFlashChunk((prev) => prev + chunk);
    };

    // Callback to merge the flash chunk into the final content.
    // (In your actual use, you might delay merging until the stream is complete.)
    const mergeFlashChunk = () => {
      setFinalContent((prev) => prev + flashChunk);
      setFlashChunk("");
    };

    // Clean the final content.
    const cleanedContent = useMemo(() => {
      logTrace("Before cleaning final content:", finalContent);
      let text = finalContent
        .replace(/<DONE>/g, "")
        .replace(/\\\\/g, "\\")
        .replace(/\\\|/g, "|")
        .replace(/\\u003e/g, ">")
        .replace(/\\u003c/g, "<")
        .replace(/\u003e/g, ">")
        .replace(/\u003c/g, "<")
        .replace(/\\u0026/g, "&")
        .replace(/\\n/g, "\n")
        .replace(/\|\s?\n\n/g, "|\n\n")
        .replace(/^\|(.+)\|\s*\n{2,}/gm, "|$1|\n\n\n")
        .replace(/(?<!\$)(?<![\\\(\$])\$\$(\d[\d,.]*)/g, "\$$1")
        .replace(/"&lt;/g, "<")
        .replace(/&gt;"/g, ">");
      logTrace("After cleaning final content:", text);
      return text;
    }, [finalContent]);

    // Custom Link wrapper.
    const CustomLinkWrapper = (props: any) => {
      return <CustomLink {...props} wrappedParams={wrappedParams} />;
    };

    // (Retain your helper functions: extractMathFormulas, removeUnnecessaryBackslashes, etc.)
    const extractMathFormulas = (text: string) => {
      let formattedText = "";
      let lastIndex = 0;
      const formulaRegex =
        /(\\text\{.*?\}|\\frac\{.*?\}\{.*?\}|\\sum\{.*?\}|\\sqrt\{.*?\}|\\\[[\s\S]*?\\\]|\$\$[\s\S]*?\$\$|\$.*?\$|\\\(.*?\\\))/g;
      let match;
      while ((match = formulaRegex.exec(text)) !== null) {
        const rawFormula = match[0].trim();
        const index = match.index;
        if (lastIndex < index) {
          formattedText += text.substring(lastIndex, index);
        }
        let formattedFormula = rawFormula
          .replace(/\\\[/g, "")
          .replace(/\\\]/g, "")
          .replace(/\\\(/g, "")
          .replace(/\\\)/g, "")
          .replace(/\\frac/g, "\\dfrac")
          .replace(/\\text/g, "\\mathrm")
          .replace(/\\frac/g, "\\frac")
          .replace(/\\dfrac/g, "\\dfrac")
          .replace(/\\sum/g, "\\sum")
          .replace(/\\sqrt/g, "\\sqrt")
          .replace(/\\left/g, "\\left")
          .replace(/\\right/g, "\\right")
          .replace(/\^\{(.*?)\}/g, "^{$1}")
          .replace(/_(\w)/g, "_{ $1 }")
          .replace(/\s+/g, " ")
          .trim();
        const hasDollarSigns =
          formattedFormula.startsWith("$") ||
          formattedFormula.startsWith("$$");
        if (!hasDollarSigns) {
          formattedText += `$${formattedFormula}$`;
        } else {
          formattedText += formattedFormula;
        }
        lastIndex = index + rawFormula.length;
      }
      if (lastIndex < text.length) {
        formattedText += text.substring(lastIndex);
      }
      const endsWithDoubleQuotes =
        text.endsWith("question") || text.endsWith("action");
      if (endsWithDoubleQuotes) {
        formattedText = removeUnnecessaryBackslashes(formattedText);
      }
      return formattedText;
    };

    function detectBackslashContext(text: string) {
      const results: { type: string; value: string; index: number }[] = [];
      const regexPatterns = [
        { type: "JSON Escape", regex: /\\\"/g },
        { type: "JSON Escape", regex: /\\\\/g },
        { type: "ASCII Control", regex: /\\[nrtbf]/g },
        { type: "HTML Entity", regex: /\\&[a-zA-Z]+;/g },
        { type: "LaTeX Inline", regex: /\\\((.*?)\\\)/g },
        { type: "LaTeX Block", regex: /\\\[(.*?)\\\]/g },
        { type: "LaTeX Command", regex: /\\[a-zA-Z]+/g },
        { type: "General Escape", regex: /\\/g },
      ];
      regexPatterns.forEach(({ type, regex }) => {
        let match;
        while ((match = regex.exec(text)) !== null) {
          results.push({ type, value: match[0], index: match.index });
        }
      });
      return results;
    }

    function removeUnnecessaryBackslashes(text: string) {
      const contextResults = detectBackslashContext(text);
      const backslashWordRegex = /\s\\(\w+)(?:\s+(\w+))?\\\s/g;
      return text.replace(
        backslashWordRegex,
        (match, p1, p2, offset) => {
          const inContext = contextResults.some(
            (result) =>
              offset >= result.index &&
              offset < result.index + result.value.length
          );
          if (inContext) {
            return ` ${p1}${p2 ? " " + p2 : ""} `;
          }
          return match;
        }
      );
    }

    function base64ToImageURL(base64String: string) {
      const imageContent = `data:image/png;base64,${base64String}`;
      const mimeType = imageContent.split(";")[0].slice(5);
      const imageData = imageContent.split(",")[1];
      const buffer = new Uint8Array(
        atob(imageData)
          .split("")
          .map((c) => c.charCodeAt(0))
      );
      const blob = new Blob([buffer], { type: `image/${mimeType}` });
      return URL.createObjectURL(blob);
    }

      useEffect(() => {
    const updateImageSrc = () => {
      const images = document.querySelectorAll('img[alt^="docimage"]');
      images.forEach((img) => {
        const altText = img.getAttribute("alt");
        const action = appData.ActionList?.find(
          (a: any) => a.className === altText
        );
       logDebug("action.srcUrl:", action?.srcUrl," altText:", altText);
      if (altText) {
        img.setAttribute("alt", altText.replace("docimage_", ""));
        img.setAttribute("class", altText.replace("docimage_", ""));
      }
        if (action && action.srcUrl) {
          img.setAttribute("src", action.srcUrl);
          (img as HTMLImageElement).style.display = "block";
          (img as HTMLImageElement).style.margin = "0 auto";
          (img as HTMLImageElement).style.borderRadius = "0";
        }
      });
    };

    updateImageSrc();
  }, [appData.ActionList]);

    const validateCustomTags = (text: string): string => {
      text = text
        .replace(/\\\\</g, "<")
        .replace(/\\</g, "<")
        .replace(/\\\\>/g, ">")
        .replace(/\\>/g, ">");
      text = text.replace(
        /(?:\\u003c|<)(u)(?:\\u003e|>)(.*?)(?:\\u003c|<\/)(?:u)(?:\\u003e|>)/g,
        (match, tag, content) => {
          return content.includes("?")
            ? `<question>${content}</question>`
            : `<action>${content}</action>`;
        }
      );
      text = text
        .replace(/^\d+\.\s*(?=<action>|<question>)/gm, "")
        .replace(/^\-\s*(?=<action>|<question>)/gm, "")
        .replace(/\n{2,}(?=<action>|<question>)/g, "\n")
        .replace(/\n(?=<action>|<question>)/g, "")
        .replace(/(<action>|<question>)/g, "custom123tag321qazxsw$1")
        .replace(/<f>/g, "")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/<br>/g, "")
        .replace(/"(?=\s*custom123tag321qazxsw<question>)/g, "")
        .replace(/(?<=custom123tag321qazxsw<\/question>)"/g, "")
        .replace(/"<"/g, "<")
        .replace(/">"/g, ">")
        .replace(/\n{3,}/g, "\n\n");
      for (let i = 0; i < (appData?.actionDataContextProps?.length || 0); i++) {
        const actionTag = appData?.actionDataContextProps?.[i]?.actionTag;
        if (text.includes(actionTag)) {
          const className = appData?.actionDataContextProps?.[i]?.className ?? "";
          const imgsrc = appData?.actionDataContextProps?.[i]?.inputs?.src ?? "";
          if (text.includes(actionTag) && imgsrc.length > 0 && text.includes("_image_")) {
            const existingAction = appData?.ActionList?.find((action) => action?.className === "docimage_" + className);
            let urlsrc = null;
            if (!existingAction) {
                urlsrc = base64ToImageURL(imgsrc);
                appData.ActionList = [
                    ...(appData.ActionList || []),
                    { className: "docimage_" + className, srcUrl: urlsrc },
                ];
                logDebug(`Found action tag: ${className}, with src: ${urlsrc}`);
            } else {
                urlsrc = existingAction.srcUrl;
            }
            if(text.includes("/p")) {
                logWarning("image is breaking:", text);
            }
            const markDownIMGsyntax = ` ![${"docimage_" + className}](${urlsrc}) `
            const escapedActionTag = actionTag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                text = text.replace(new RegExp(`<p>${escapedActionTag}</p>`, 'g'), actionTag);
            text = text.replace(
              actionTag,
               markDownIMGsyntax
            );
            const action_message = `ACTION - step 2 formatting action ${markDownIMGsyntax} ${actionTag} - message:${sessionStorage.getItem("message_id")} - sessionID:${sessionStorage.getItem("session_ID")}`;
            logDebug(action_message);
            logDebug("setting image syntax:", markDownIMGsyntax)
          }
          if (text.includes(actionTag) && text.includes("chart")) {
            const action = appData?.actionDataContextProps?.[i]?.action;
            const chart_config = appData?.actionDataContextProps?.[i]?.inputs;
            if (
              !appData.ActionList?.some(
                (a) => a.className === "chart_" + className
              )
            ) {
              appData.ActionList = [
                ...(appData.ActionList || []),
                { className: "chart_" + className, srcUrl: chart_config },
              ];
            }
            const markDownIMGsyntax = `![${"chart_" + className}](${action})`
            text = text.replace(
              actionTag,
              markDownIMGsyntax
            );
            const action_message = `ACTION - step 2 formatting action ${markDownIMGsyntax} ${actionTag} - message:${sessionStorage.getItem("message_id")} - sessionID:${sessionStorage.getItem("session_ID")}`;
            logDebug(action_message);
          }
        }
      }
      logTrace("Updated text:", text);
      text = extractMathFormulas(text);
      return text;
    };

    return (
      <div className="markdownFormattedResponse">
        <ReactMarkdown
          skipHtml={false}
          rehypePlugins={[rehypeKatex, rehypeRaw]}
          remarkPlugins={[
            [remarkGfm, { singleTilde: false }],
            remarkMath,
            supersub,
            remarkDeflist,
            [remarkParse, {}],
            [actionQuestionPlugin, { cleanedText: cleanedContent }],
            [replaceUnicodeSymbols, {}],
          ]}
          components={{
            a: CustomLinkWrapper,
            ...componentsContainer(questionHandler, load),
          }}
        >
          {validateCustomTags(cleanedContent)}
        </ReactMarkdown>
        {/* Debug: render flashChunk below final content.
            For debugging, the flash chunk is given a temporary red color.
            Once visible, remove the color or adjust as needed. */}
        {/*{flashChunk && (*/}
        {/*  <span className="flash-chunk" style={{ display: "inline-block", marginLeft: "5px", backgroundColor: "rgba(255,0,0,0.2)" }}>*/}
        {/*    {flashChunk}*/}
        {/*  </span>*/}
        {/*)}*/}
      </div>
    );
  }
);

export default React.memo(FormattedRes);
